"use client";

import {
  Box,
  Button,
  Card,
  CardContent,
  Container,
  Grid,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { useMemo } from "react";
import SalifyFallback from "src/lib/components/salify-fallback/default";
import SalifyLink from "src/lib/components/link/salify-link";
import { toAssetImages } from "src/lib/utils";
import {
  LayoutViewMode,
  ResponsivePropsConfig,
  resolveResponsiveProps,
  splitDesktopAndResponsive,
} from "src/lib/utils/responsive-props";

export type DsBlogCardButton = {
  text: string;
  link: string;
};

export type DsBlogPostItem = {
  slug: string;
  name: string;
  description?: string;
  thumbnail?: string;
  createdAt?: string | Date;
};

type DsBlogCardViewportData = {
  title?: string;
  heading1?: string;
  itemCount?: number;
  detailLinkText?: string;
  dataOutput?: string;
  button?: DsBlogCardButton;
};

export interface VariantProps extends DsBlogCardViewportData {
  responsive?: ResponsivePropsConfig<DsBlogCardViewportData>;
}

const ACCENT = "#FFA411";
const CARD_BORDER = "#e8e8e8";

const useLayoutViewMode = (): LayoutViewMode => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("tablet"));
  const isTablet = useMediaQuery(theme.breakpoints.between("tablet", "laptop"));
  if (isMobile) return "mobile";
  if (isTablet) return "tablet";
  return "desktop";
};

const formatPostDate = (value?: string | Date) => {
  if (!value) return "";
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return `${date.getDate()}/${date.getMonth() + 1}/${date.getFullYear()}`;
};

const BlogCard = ({ post, detailLinkText }: { post: DsBlogPostItem; detailLinkText: string }) => {
  const thumbUrl = post.thumbnail?.trim() ? toAssetImages(post.thumbnail as string) : "";

  return (
    <Card
      elevation={0}
      sx={{
        height: "100%",
        borderRadius: 2,
        border: `1px solid ${CARD_BORDER}`,
        bgcolor: "#fff",
        boxShadow: "0 2px 12px rgba(0,0,0,0.06)",
        overflow: "hidden",
      }}
    >
      {thumbUrl ? (
        <Box
          component="img"
          src={thumbUrl}
          alt={post.name}
          loading="lazy"
          sx={{
            width: "100%",
            height: { xs: 180, laptop: 200 },
            objectFit: "cover",
            display: "block",
          }}
        />
      ) : (
        <Box sx={{ width: "100%", height: { xs: 180, laptop: 200 }, bgcolor: "grey.100" }} />
      )}
      <CardContent sx={{ p: { xs: 2, laptop: 2.5 } }}>
        <Typography
          component="h3"
          sx={{
            fontWeight: 700,
            fontSize: { xs: 15, laptop: 16 },
            lineHeight: 1.4,
            color: "text.primary",
            mb: 1,
            display: "-webkit-box",
            WebkitLineClamp: 2,
            WebkitBoxOrient: "vertical",
            overflow: "hidden",
          }}
        >
          {post.name}
        </Typography>
        {post.description ? (
          <Typography
            sx={{
              fontSize: { xs: 13, laptop: 14 },
              lineHeight: 1.65,
              color: "text.secondary",
              mb: 2,
              display: "-webkit-box",
              WebkitLineClamp: 3,
              WebkitBoxOrient: "vertical",
              overflow: "hidden",
            }}
          >
            {post.description}
          </Typography>
        ) : null}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 1,
          }}
        >
          <Typography sx={{ fontSize: 12, color: "text.disabled" }}>
            {formatPostDate(post.createdAt)}
          </Typography>
          <SalifyLink href={`/${post.slug}`}>
            <Typography
              sx={{
                fontSize: 13,
                fontWeight: 600,
                color: "text.primary",
                "&:hover": { color: ACCENT },
              }}
            >
              {detailLinkText} →
            </Typography>
          </SalifyLink>
        </Box>
      </CardContent>
    </Card>
  );
};

const DsBlogCardDefaultV2 = (props: VariantProps) => {
  const viewMode = useLayoutViewMode();
  const { desktop, responsive } = splitDesktopAndResponsive(
    props as unknown as Record<string, unknown>,
  );

  const data = useMemo((): DsBlogCardViewportData => {
    const resolved = resolveResponsiveProps(
      {
        title: (desktop.title as string) || "",
        heading1: (desktop.heading1 as string) || "",
        itemCount: Number(desktop.itemCount ?? 4),
        detailLinkText: (desktop.detailLinkText as string) || "Chi tiết",
        dataOutput: (desktop.dataOutput as string) || "articles",
        button: desktop.button as DsBlogCardButton | undefined,
      },
      responsive,
      viewMode,
      ["title", "heading1", "itemCount", "detailLinkText", "dataOutput", "button"],
    );

    return {
      title: String(resolved.title || ""),
      heading1: String(resolved.heading1 || ""),
      itemCount: Number(resolved.itemCount || 4),
      detailLinkText: String(resolved.detailLinkText || "Chi tiết"),
      dataOutput: String(resolved.dataOutput || "articles"),
      button: resolved.button as DsBlogCardButton | undefined,
    };
  }, [desktop, responsive, viewMode]);

  const outputKey = data.dataOutput || "articles";
  const postsRaw =
    (SalifyFallback.useSelect(`output:${outputKey}.data`) as DsBlogPostItem[] | undefined) ?? [];
  const itemCount = Math.max(1, data.itemCount || 4);
  const posts = postsRaw.slice(0, itemCount);
  const detailLinkText = data.detailLinkText || "Chi tiết";

  return (
    <Box component="section" id="ds-blog-card" sx={{ py: { xs: 5, laptop: 7 }, bgcolor: "#fff" }}>
      <Container maxWidth="lg">
        <Box sx={{ textAlign: "center", mb: { xs: 4, laptop: 5 } }}>
          {data.title ? (
            <Typography
              component="p"
              sx={{
                color: ACCENT,
                fontSize: { xs: 13, laptop: 14 },
                fontWeight: 700,
                letterSpacing: "0.08em",
                textTransform: "uppercase",
                mb: 1,
              }}
            >
              {data.title}
            </Typography>
          ) : null}
          {data.heading1 ? (
            <Typography
              component="h2"
              sx={{
                fontWeight: 800,
                fontSize: { xs: 24, tablet: 30, laptop: 34 },
                lineHeight: 1.2,
                color: "text.primary",
                letterSpacing: "-0.02em",
                textTransform: "uppercase",
              }}
            >
              {data.heading1}
            </Typography>
          ) : null}
        </Box>

        {posts.length > 0 ? (
          <Grid container spacing={{ xs: 2, laptop: 3 }}>
            {posts.map((post) => (
              <Grid item xs={12} tablet={6} laptop={3} key={post.slug}>
                <BlogCard post={post} detailLinkText={detailLinkText} />
              </Grid>
            ))}
          </Grid>
        ) : null}

        {data.button?.text ? (
          <Box sx={{ display: "flex", justifyContent: "center", mt: { xs: 4, laptop: 5 } }}>
            <SalifyLink href={data.button.link || "#"}>
              <Button
                variant="contained"
                sx={{
                  bgcolor: ACCENT,
                  color: "#111",
                  fontWeight: 700,
                  px: 4,
                  py: 1.35,
                  borderRadius: 1,
                  boxShadow: "none",
                  textTransform: "none",
                  "&:hover": { bgcolor: "#e69500", boxShadow: "none" },
                }}
              >
                {data.button.text}
              </Button>
            </SalifyLink>
          </Box>
        ) : null}
      </Container>
    </Box>
  );
};

export default DsBlogCardDefaultV2;
